import {
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
  Image,
  ImageStyle,
} from 'react-native';
import React, {useState} from 'react';
import {spacing} from 'theme/Units';
import {colors} from 'theme/Colors';
import {Button} from 'ui-kits';
import {TestmonialCard} from './Card';
import {mergeDeepRight} from 'ramda';
import {staticDataForScreen} from 'constants/DefaultValues';

// importing testimonial data
const {testimonials: TESTIMONIALS} = staticDataForScreen.landing_screen;

type styleType = {
  container: ViewStyle;
  heading: TextStyle;
  heading2: TextStyle;
  paragraph: TextStyle;
  testimonialContainer: ViewStyle;
  activeCard: ViewStyle;
  arrowIcons: ImageStyle;
  resolveButton: ViewStyle,
  arrowButtonWrapper: ViewStyle,
  brandBlue: TextStyle;
};

type propType = Readonly<{
  openModal: () => void;
}>;

export function Testimonials(props: propType) {
  const {openModal} = props;
  const [currentIndex, setCurrentIndex] = useState(0);

  /**
   * responsiveness logic
   */
  const SCREEN_WIDTH = useWindowDimensions().width;

  let overrideStyles = structuredClone(baseStyles); // deep copy of base to override styles from base styles

  if (SCREEN_WIDTH < 1024) {
    overrideStyles.heading.fontSize = 48;
  }

  if (SCREEN_WIDTH < 720) {
    overrideStyles.heading.fontSize = 42;
    overrideStyles.paragraph.fontSize = 18;
    overrideStyles.activeCard.bottom = 0;
  }
  if (SCREEN_WIDTH < 640) {
    overrideStyles.heading.fontSize = 36;
    overrideStyles.paragraph.fontSize = 16;
  }

  if (SCREEN_WIDTH < 480) {
    overrideStyles.heading.fontSize = 30;
    overrideStyles.paragraph.fontSize = 14;
    overrideStyles.container.paddingVertical = spacing['6'];
    overrideStyles.container.paddingHorizontal = spacing['6'];
  }
  if (SCREEN_WIDTH < 360) {
    overrideStyles.heading.fontSize = 24;
    overrideStyles.container.paddingVertical = spacing['4'];
    overrideStyles.container.paddingHorizontal = spacing['4'];
  }

  // merge styles with override styles
  const styles: styleType = mergeDeepRight(baseStyles, overrideStyles) as any; // any type fixes error squigles

  // Handlers for shifting testimonials
  const handlePrevious = () => {
    setCurrentIndex(prevIndex =>
      prevIndex === 0 ? TESTIMONIALS.length - 1 : prevIndex - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex(prevIndex =>
      prevIndex === TESTIMONIALS.length - 1 ? 0 : prevIndex + 1,
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  function getCurrentElementsFromArray(currentIndex: number): any {
    const total = TESTIMONIALS.length;

    const prevIndex = (currentIndex - 1 + total) % total; // Circular index for the previous element
    const nextIndex = (currentIndex + 1) % total; // Circular index for the next element

    return [
      TESTIMONIALS[prevIndex], // Previous testimonial
      TESTIMONIALS[currentIndex], // Current testimonial
      TESTIMONIALS[nextIndex], // Next testimonial
    ];
  }
  const testimonials = getCurrentElementsFromArray(currentIndex);

  const leftArrowButton = (
    <TouchableOpacity onPress={handlePrevious}>
      <Image
        style={styles.arrowIcons}
        source={require('assets/icons/arrow_circle_left_purple@2x.png')}
      />
    </TouchableOpacity>
  );
  const rightArrowButton = (
    <TouchableOpacity onPress={handleNext}>
      <Image
        style={styles.arrowIcons}
        source={require('assets/icons/arrow_circle_right_purple@2x.png')}
      />
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <Text style={styles.heading}>
        What
        <Text style={styles.brandBlue}> Developers </Text>
        Are Saying
      </Text>

      <Text style={styles.paragraph}>
        Every developer has been there. You're ready to dive in, write amazing
        code, and solve problems—but the reality is often messier. These are the
        everyday frustrations that hold teams back
      </Text>

      {/* testominial cards */}
      <View style={styles.testimonialContainer}>
        <TestmonialCard
          title={testimonials[0].title}
          description={testimonials[0].description}
        />
        <View>
          <TestmonialCard
            title={testimonials[1].title}
            description={testimonials[1].description}
            variant={SCREEN_WIDTH < 720 ? 'unactive' : 'active'}
            style={styles.activeCard}
          />
        </View>
        <TestmonialCard
          title={testimonials[2].title}
          description={testimonials[2].description}
        />
      </View>

      {/* arrow buttons */}
      <View style={styles.arrowButtonWrapper}>
        {leftArrowButton}
        {rightArrowButton}
      </View>

      {/* 'resolve challenges today' button */}
      <Button
        style={styles.resolveButton}
        variant="primary"
        text="Resolve Challenges Today"
        // onPress={openModal}
      />
    </View>
  );
}

const baseStyles: styleType = {
  brandBlue: {color: colors.palette.brandBlue},
  arrowButtonWrapper: {flexDirection: 'row', gap: spacing['4']},
  resolveButton: {
    width: 250,
    paddingVertical: spacing['6'],
  },
  container: {
    backgroundColor: '#121212',
    alignItems: 'center',
    paddingVertical: spacing['14'],
    paddingHorizontal: spacing['16'],
    gap: spacing['8'],
  },
  activeCard: {
    bottom: spacing['8'],

    position: 'relative',
  },
  heading: {
    color: 'white',
    fontSize: 52,
    fontWeight: '600',
    textAlign: 'center',
  },
  heading2: {
    color: 'white',
    fontSize: 32,
    fontWeight: '600',
  },
  paragraph: {
    color: 'white',
    fontSize: 20,
    fontWeight: '400',
    textAlign: 'center',
    maxWidth: 800,
  },
  arrowIcons: {
    width: 50,
    height: 50,
    resizeMode: 'contain',
  },
  testimonialContainer: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center',
    gap: spacing['12'],
    marginTop: spacing['16'],
  },
};
